import { useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { Box, makeStyles, TableHead, TableRow, TableCell, Table, TableBody } from '@material-ui/core';
import _ from 'lodash';
import Text from '../../../../../components/Text';
import StyleButton from '../../../../../components/StyleButton';
import { AddIcon, EditIcon } from '../../../../../components/Icon';
import { eadmissionAPI } from '../../../../../apis';
import { handleDeleteEmbedBug } from '../../../../../utils/newBedStatusUtil';
import Row from './Row';
import DefaultValue from './popups/DefaultValue';
import SpecialtiesPopup from './popups/SpecialtiesPopup';

const useStyles = makeStyles((theme) => ({
  table: {
    borderSpacing: '0',
    borderCollapse: 'unset',
    '& .MuiTableCell-head': {
      color: '#538FFF',
      fontFamily: 'NotoSans-Bold',
      fontSize: '0.75rem',
      textTransform: 'uppercase',
      borderBottom: 'none !important',
      padding: '0 1rem 1rem 0',
    },
    '& .MuiTableCell-body': {
      borderBottom: 'solid 1px #E4E7EB',
      verticalAlign: 'middle',
      padding: '1.25rem 1rem 1.75rem 0',
      color: '#2E4469',
      fontSize: '1rem',
      fontFamily: 'NotoSans-Medium',
    },
  },
}));

// 整理ward location资料给新增和编辑接口共用。
const handleRevampWardLoc = (wardLocItem) => {
  let newWardLoc = {
    gender: wardLocItem?.gender,
    id: wardLocItem?.id,
    ipasHospCode: wardLocItem?.ipasHospCode,
    ipasWardLocation: wardLocItem?.ipasWardLocation,
    occThreshold: wardLocItem?.occThreshold,
    unitId: wardLocItem?.unitId,
    ageBelow: '',
    ageOnAbove: '',
  };
  if (wardLocItem?.range && wardLocItem?.age) {
    newWardLoc[wardLocItem?.range] = wardLocItem?.age;
  }
  return newWardLoc;
};

// 整理新增和编辑specialty接口共用的参数。
const buildSpecialtyParams = (form) => {
  let additionalRemark = '';
  let specialtyWardloc = [];

  if (form.additionalRemark !== '') {
    additionalRemark = handleDeleteEmbedBug(form.additionalRemark);
  }
  if (_.isArray(form?.specialtyWardloc) && form?.specialtyWardloc?.length > 0) {
    specialtyWardloc = _.map(form?.specialtyWardloc, (item) => handleRevampWardLoc(item));
  }

  return { ...form, isMatchedAge: form?.isMatchedAge ? 1 : 0, additionalRemark, specialtyWardloc };
};

const Mapping = ({ mappingRef }) => {
  const classes = useStyles();
  const [defaultValue, setDefaultValue] = useState(false);
  const [addSpecialties, setAddSpecialties] = useState(false);
  const [editSpecialties, setEditSpecialties] = useState(false);
  const [data, setData] = useState([]);
  const [pageInfo, setPageInfo] = useState({
    pageNo: 1,
    pageSize: 50,
  });
  const [editId, setEditId] = useState('');
  const [total, setTotal] = useState(0);

  const getSpecialtyList = useCallback(async () => {
    try {
      const params = {
        pageNo: pageInfo.pageNo,
        pageSize: pageInfo.pageSize,
      };
      const { result } = await eadmissionAPI.getSpecialtyList(params);
      setTotal(result.count);
      if (pageInfo.pageNo === 1) {
        setData(result?.data);
      } else {
        setData((preData) => [...preData, ...result.data]);
      }
    } catch (error) {
      console.log(error);
    }
  }, [pageInfo]);

  useEffect(() => {
    getSpecialtyList();
  }, [getSpecialtyList]);

  const handleCreateSpecial = useCallback(
    (form) => async () => {
      try {
        await eadmissionAPI.createSpecialty(buildSpecialtyParams(form));
        setAddSpecialties(false);
        getSpecialtyList();
      } catch (error) {}
    },
    [getSpecialtyList]
  );

  const handleUpdateSpecial = useCallback(
    (form) => async () => {
      try {
        await eadmissionAPI.updateSpecialty(buildSpecialtyParams(form));
        setEditSpecialties(false);
        setPageInfo((o) => ({
          ...o,
          pageNo: 1,
        }));
      } catch (error) {}
    },
    []
  );

  const turnPage = () => {
    if (total > data.length) {
      setPageInfo((o) => ({
        ...o,
        pageNo: o.pageNo + 1,
      }));
    }
  };

  useImperativeHandle(mappingRef, () => ({
    turnPage,
  }));

  return (
    <Box>
      {/* Popups */}
      {defaultValue && <DefaultValue handleClose={() => setDefaultValue(false)} />}
      {addSpecialties && (
        <SpecialtiesPopup handleClose={() => setAddSpecialties(false)} handleConfirm={handleCreateSpecial} />
      )}
      {editSpecialties && (
        <SpecialtiesPopup
          handleClose={() => setEditSpecialties(false)}
          handleConfirm={handleUpdateSpecial}
          specialtyId={editId}
          type="Edit"
        />
      )}

      <Box display="flex" alignItems="center" justifyContent="space-between" mb="1.38rem">
        <Text color="#333" fontSize="1.5rem" fontFamily="NotoSans-Bold">
          Specialty & Ward Mapping
        </Text>
        <Box display="flex" gridGap="1.25rem">
          <StyleButton
            text="Set Default Value"
            Icon={EditIcon}
            width="13.2rem"
            border="none"
            onClick={() => setDefaultValue(true)}
          />
          <StyleButton
            text="Add Specialty"
            Icon={AddIcon}
            width="12rem"
            type="fill"
            onClick={() => setAddSpecialties(true)}
          />
        </Box>
      </Box>
      <Table className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell>Specialty</TableCell>
            <TableCell>Sub Specialty</TableCell>
            <TableCell>isolation room</TableCell>
            <TableCell>Ward Assignment By</TableCell>
            <TableCell>Active</TableCell>
            <TableCell>Gender</TableCell>
            <TableCell>Hosp</TableCell>
            <TableCell>IPAS Ward location</TableCell>
            <TableCell>Ward Place Unit</TableCell>
            <TableCell>Occupancy threshold</TableCell>
            <TableCell>Age</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((item) => (
            <Row
              {...item}
              handleEdit={() => {
                setEditSpecialties(true);
                setEditId(item.specialtyId);
              }}
              key={item.specialtyId}
            />
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

export default Mapping;
